import { createContext, useContext, useMemo, useState } from "react";
import type { Dispatch, ReactNode } from "react";
import type { Session } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/mutation";

export type StoredSession = Pick<Session, "user" | "exp"> | null;

export type SessionContextType = {
  session: StoredSession | null;
  setSession: Dispatch<React.SetStateAction<StoredSession | null>>;
  hostJwt: string | null;
  setHostJwt: (jwt: string | null) => void;
};

export const SessionContext = createContext<SessionContextType | null>(null);
export const useSessionContext = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("Missing SessionContext.Provider");
  }
  return context;
};

/**
 * Sets and gets the session returned from dpGetSession.
 * NB: We don't want to trigger auth until the TabsLayout is mounted to keep the Launcher button render lightweight
 * so setSession is triggered by InitAuth.
 */
export const SessionProvider = ({
  children,
  default: defaultValue,
}: {
  children: ReactNode;
  default?: StoredSession;
}) => {
  const [session, setSession] = useState<StoredSession | null | null>(defaultValue || null);
  const [hostJwt, setHostJwt] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      session,
      setSession,
      hostJwt,
      setHostJwt,
    }),
    [session, hostJwt],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export async function getOrUpdateSession({
  tenantId,
  messengerId,
  messengerToken,
  auth,
}: {
  tenantId: string;
  messengerId: string;
  messengerToken: string;
  auth: string;
}): Promise<Session | null> {
  try {
    const origin = import.meta.env.VITE_MESSENGER_API_ORIGIN ?? window.location.origin;
    const path = import.meta.env.VITE_MESSENGER_API_PATH ?? "";
    const res = await fetch(
      `${origin}${path}/${tenantId}/messenger/${messengerId}/user/auth/session`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Deskpro-Token": messengerToken,
        },
        body: JSON.stringify({
          token: auth,
        }),
      },
    );

    const data = await res.json();

    const session = "user" in data ? data : null;
    return session;
  } catch (error) {
    console.error("dpGetSession error", { error });
  }
  return null;
}
